import type { AvatarConfig } from './avatars';
import { getActiveLayout, isBlocked, sanitizePosition, type Rect } from './map';
import { PALETTE, roundRectPath, withRotation, withRotationAt } from './render';
import { normalizeDeg, rotatePoint } from './rotation';

/**
 * Digital Company — live workstations on the building map.
 *
 * A workstation is anchored at its "stand point": where the worker stands.
 * The bench itself is drawn just above that point (like the desks, whose
 * chairs sit south of them), so a pinned character reads as standing at
 * the bench facing it.
 */

export interface FloorStation {
  id: string;
  name: string;
  /** Stand point, world units (unrotated; the bench sits north of this). */
  x: number;
  y: number;
  /** Bench footprint; defaults to BENCH when omitted. */
  w?: number;
  h?: number;
  /** Clockwise degrees around the bench centre. */
  rotation?: number;
  /** False when the manager has not placed it and it sits in a default slot. */
  placed: boolean;
}

export interface PinnedWorker {
  userId: string;
  name: string;
  config: AvatarConfig;
  stationId: string;
  stationName: string;
  x: number;
  y: number;
  itemName: string | null;
  /** Units completed this shift. */
  unitsDone: number;
  /** Epoch ms when the current unit started (break time is not subtracted here). */
  unitStartedAt: number;
  onBreak: boolean;
  breakLabel: string | null;
}

export const BENCH = { w: 32, h: 14, gap: 8 } as const;

function stationSize(w?: number, h?: number): { w: number; h: number } {
  return { w: w && w > 0 ? w : BENCH.w, h: h && h > 0 ? h : BENCH.h };
}

/** The bench rectangle for a station anchored at (x, y). */
export function benchRect(x: number, y: number, w?: number, h?: number): Rect {
  const s = stationSize(w, h);
  return { x: x - s.w / 2, y: y - BENCH.gap - s.h, w: s.w, h: s.h };
}

/** Bench + the spot the worker stands on, for hit-testing in arrange mode. */
export function stationHitRect(x: number, y: number, w?: number, h?: number): Rect {
  const b = benchRect(x, y, w, h);
  return { x: b.x - 2, y: b.y - 2, w: b.w + 4, h: b.h + BENCH.gap + 10 };
}

/** Where the worker is drawn after the bench has been rotated around its centre. */
export function stationStandPoint(station: FloorStation): { x: number; y: number } {
  const rot = station.rotation ?? 0;
  if (!rot) return { x: station.x, y: station.y };
  const b = benchRect(station.x, station.y, station.w, station.h);
  return rotatePoint(station.x, station.y, b.x + b.w / 2, b.y + b.h / 2, rot);
}

/** Face the bench from the stand point. */
export function workerFacingAtStation(rotation?: number): 'up' | 'down' | 'left' | 'right' {
  const n = normalizeDeg(rotation ?? 0);
  if (n >= 315 || n < 45) return 'up';
  if (n < 135) return 'left';
  if (n < 225) return 'down';
  return 'right';
}

/** Hit-test a station as a rigid body rotated around the bench centre. */
export function pointHitsStation(
  station: FloorStation,
  standX: number,
  standY: number,
  wx: number,
  wy: number,
): boolean {
  const b = benchRect(standX, standY, station.w, station.h);
  const rot = station.rotation ?? 0;
  const local = rotatePoint(wx, wy, b.x + b.w / 2, b.y + b.h / 2, -rot);
  const hit = stationHitRect(standX, standY, station.w, station.h);
  return local.x >= hit.x && local.x <= hit.x + hit.w && local.y >= hit.y && local.y <= hit.y + hit.h;
}

/**
 * Where unplaced stations go until a manager drags them somewhere: the open
 * floor in the middle of the warehouse, then the strip by the dock doors.
 */
export const DEFAULT_SLOTS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 150, y: 322 }, { x: 195, y: 322 }, { x: 240, y: 322 }, { x: 285, y: 322 }, { x: 330, y: 322 }, { x: 375, y: 322 },
  { x: 150, y: 380 }, { x: 195, y: 380 }, { x: 240, y: 380 }, { x: 330, y: 380 }, { x: 375, y: 380 },
  { x: 445, y: 330 }, { x: 490, y: 330 }, { x: 530, y: 330 },
  { x: 445, y: 385 }, { x: 490, y: 385 }, { x: 530, y: 385 },
];

/** Keep a dropped bench inside the building and off the walls / racks. */
export function clampStationPosition(
  x: number,
  y: number,
  options?: { free?: boolean; w?: number; h?: number },
): { x: number; y: number } {
  const s = stationSize(options?.w, options?.h);
  const warehouse = getActiveLayout().zones.find((z) => z.id === 'warehouse');
  const bounds = options?.free || !warehouse
    ? { x: 20, y: 20, w: 560, h: 1010 }
    : warehouse;
  const minX = bounds.x + s.w / 2 + 10;
  const maxX = bounds.x + bounds.w - s.w / 2 - 8;
  const minY = bounds.y + s.h + BENCH.gap + 12;
  const maxY = bounds.y + bounds.h - 10;
  const cx = Math.max(minX, Math.min(maxX, x));
  const cy = Math.max(minY, Math.min(maxY, y));
  if (!isBlocked(cx, cy)) return { x: Math.round(cx), y: Math.round(cy) };
  const snapped = sanitizePosition(cx, cy);
  return { x: Math.round(snapped.x), y: Math.round(snapped.y) };
}

/**
 * Resolve where each station sits: its saved floor coordinates, or the next
 * free default slot (stable across renders because stations are already in
 * `position` order).
 */
export function resolveStationPositions(
  stations: Array<{ id: string; name: string; floor_x: number | null; floor_y: number | null }>,
): FloorStation[] {
  const taken = new Set<string>();
  for (const s of stations) {
    if (s.floor_x != null && s.floor_y != null) taken.add(`${Math.round(s.floor_x)},${Math.round(s.floor_y)}`);
  }
  let slot = 0;
  const nextSlot = () => {
    while (slot < DEFAULT_SLOTS.length) {
      const c = DEFAULT_SLOTS[slot++];
      if (!taken.has(`${c.x},${c.y}`)) return c;
    }
    // More stations than slots: stack along the bottom aisle.
    const i = slot++ - DEFAULT_SLOTS.length;
    return { x: 150 + (i % 8) * 45, y: 410 };
  };
  return stations.map((s) => {
    if (s.floor_x != null && s.floor_y != null) {
      return { id: s.id, name: s.name, x: s.floor_x, y: s.floor_y, placed: true };
    }
    const c = nextSlot();
    return { id: s.id, name: s.name, x: c.x, y: c.y, placed: false };
  });
}

/**
 * Draw a workbench at a station. World space. `state` tints the tablet on
 * the bench: dark when empty, Bright Moss while someone is building,
 * Atmosphere while they are on a break.
 */
export function drawWorkbench(
  ctx: CanvasRenderingContext2D,
  station: FloorStation,
  state: 'empty' | 'working' | 'break',
  highlight = false,
) {
  const b = benchRect(station.x, station.y, station.w, station.h);
  const rot = station.rotation ?? 0;
  withRotation(ctx, b, rot, () => {
    // Contact shadow
    ctx.fillStyle = 'rgba(27,46,29,0.18)';
    roundRectPath(ctx, b.x + 1, b.y + 2, b.w, b.h, 2);
    ctx.fill();
    // Top
    roundRectPath(ctx, b.x, b.y, b.w, b.h, 2);
    ctx.fillStyle = station.placed ? '#E6D9BE' : '#EDE6D6';
    ctx.fill();
    ctx.strokeStyle = highlight ? PALETTE.humus : '#B99F75';
    ctx.lineWidth = highlight ? 1.2 : 0.8;
    if (!station.placed) ctx.setLineDash([2, 1.5]);
    ctx.stroke();
    ctx.setLineDash([]);
    // Front edge shade
    ctx.fillStyle = 'rgba(0,0,0,0.08)';
    ctx.fillRect(b.x + 0.5, b.y + b.h - 3, b.w - 1, 2.5);
    // Tablet stand (right) with a screen glow that carries the state
    const tx = b.x + b.w - 10;
    const ty = b.y + 2;
    ctx.fillStyle = PALETTE.monitor;
    roundRectPath(ctx, tx, ty, 7.5, 5.5, 1);
    ctx.fill();
    ctx.fillStyle = state === 'working' ? PALETTE.moss : state === 'break' ? '#AAD8FA' : '#55606A';
    roundRectPath(ctx, tx + 0.8, ty + 0.8, 5.9, 3.9, 0.6);
    ctx.fill();
    // Parts tray (left) and a tool
    ctx.fillStyle = '#C9D1C4';
    roundRectPath(ctx, b.x + 3, b.y + 3, 9, 6, 1);
    ctx.fill();
    ctx.fillStyle = PALETTE.boxes[(station.name.length + 1) % PALETTE.boxes.length];
    roundRectPath(ctx, b.x + 4.5, b.y + 4.5, 2.6, 3, 0.5);
    ctx.fill();
    ctx.fillStyle = PALETTE.boxes[(station.name.length + 3) % PALETTE.boxes.length];
    roundRectPath(ctx, b.x + 8, b.y + 4.5, 2.6, 3, 0.5);
    ctx.fill();
    ctx.strokeStyle = '#6B7177';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(b.x + 15, b.y + 10);
    ctx.lineTo(b.x + 21, b.y + 5);
    ctx.stroke();
    // Legs
    ctx.fillStyle = '#9C7A55';
    ctx.fillRect(b.x + 2, b.y + b.h, 2, 2.5);
    ctx.fillRect(b.x + b.w - 4, b.y + b.h, 2, 2.5);
  });
}

/** Pin outline drawn under a bench while a manager is arranging. */
export function drawStationHandle(ctx: CanvasRenderingContext2D, station: FloorStation, active: boolean) {
  const b = benchRect(station.x, station.y, station.w, station.h);
  const r = stationHitRect(station.x, station.y, station.w, station.h);
  withRotationAt(ctx, b.x + b.w / 2, b.y + b.h / 2, station.rotation ?? 0, () => {
    ctx.save();
    ctx.setLineDash([3, 2]);
    ctx.strokeStyle = active ? 'rgba(27,46,29,0.9)' : 'rgba(27,46,29,0.35)';
    ctx.lineWidth = active ? 1.2 : 0.8;
    roundRectPath(ctx, r.x, r.y, r.w, r.h, 3);
    ctx.stroke();
    ctx.restore();
  });
}
